import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import LoadingSpinner from "../components/common/LoadingSpinner";
import UserAvatar from "../components/common/UserAvatar";
import {
  PersonAdd as InviteIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Person as PersonIcon,
  Send as ResendIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { User, UserRole } from "../types";
import {
  getFamilyMembers,
  inviteToFamily,
  removeFamilyMember,
  updateFamilyMemberRole,
  getFamily,
  updateFamilyName,
  updateFamilySettings,
  resendInvitation,
} from "../services/family";

const FamilySettings: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Family data
  const [familyName, setFamilyName] = useState("");
  const [weekStartDay, setWeekStartDay] = useState(1); // Default to Monday
  const [familyMembers, setFamilyMembers] = useState<User[]>([]);

  // Dialog states
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [isEditRoleDialogOpen, setIsEditRoleDialogOpen] = useState(false);
  const [isEditFamilyNameDialogOpen, setIsEditFamilyNameDialogOpen] =
    useState(false);

  // Form states
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.CHILD);
  const [memberToRemove, setMemberToRemove] = useState<User | null>(null);
  const [memberToEdit, setMemberToEdit] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole>(UserRole.CHILD);
  const [newFamilyName, setNewFamilyName] = useState("");

  useEffect(() => {
    const fetchFamilyData = async () => {
      try {
        if (user) {
          setLoading(true);
          const [family, members] = await Promise.all([
            getFamily(user.familyId),
            getFamilyMembers(user.familyId),
          ]);
          setFamilyName(family.name);
          setWeekStartDay(family.weekStartDay ?? 1); // Default to Monday
          setFamilyMembers(members);
        }
      } catch (err) {
        setError("Failed to load family data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFamilyData();
  }, [user]);

  const handleInviteDialogOpen = () => {
    setInviteEmail("");
    setIsInviteDialogOpen(true);
  };

  const handleInviteDialogClose = () => {
    setIsInviteDialogOpen(false);
  };

  const handleRemoveDialogOpen = (member: User) => {
    setMemberToRemove(member);
    setIsRemoveDialogOpen(true);
  };

  const handleRemoveDialogClose = () => {
    setIsRemoveDialogOpen(false);
    setMemberToRemove(null);
  };

  const handleEditRoleDialogOpen = (member: User) => {
    setMemberToEdit(member);
    setSelectedRole(member.role as UserRole);
    setIsEditRoleDialogOpen(true);
  };

  const handleEditRoleDialogClose = () => {
    setIsEditRoleDialogOpen(false);
    setMemberToEdit(null);
  };

  const handleEditFamilyNameDialogOpen = () => {
    setNewFamilyName(familyName);
    setIsEditFamilyNameDialogOpen(true);
  };

  const handleEditFamilyNameDialogClose = () => {
    setIsEditFamilyNameDialogOpen(false);
  };

  const handleInvite = async () => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);

      if (!user || !inviteEmail) {
        throw new Error("Missing required information");
      }

      await inviteToFamily(user.familyId, inviteEmail, inviteRole);
      setSuccess(
        `Invitation sent to ${inviteEmail} as ${getRoleLabel(inviteRole)}`
      );
      handleInviteDialogClose();

      // Refresh family members list
      const members = await getFamilyMembers(user.familyId);
      setFamilyMembers(members);
    } catch (err) {
      setError("Failed to send invitation");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (
    invitationId: string,
    email: string,
    role: string
  ) => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);

      if (!user) {
        throw new Error("Missing required information");
      }

      // Use dedicated resend service to update existing invitation
      await resendInvitation(invitationId);
      setSuccess(`Invitation resent to ${email}`);

      // Refresh family members list to show updated invitation
      const members = await getFamilyMembers(user.familyId);
      setFamilyMembers(members);
    } catch (err) {
      setError("Failed to resend invitation");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async () => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);

      if (!user || !memberToRemove) {
        throw new Error("Missing required information");
      }

      await removeFamilyMember(user.familyId, memberToRemove.userId);

      // Update local state to remove the member
      setFamilyMembers(
        familyMembers.filter((m) => m.userId !== memberToRemove.userId)
      );

      setSuccess(
        `${memberToRemove.firstName} ${memberToRemove.lastName} removed from family`
      );
      handleRemoveDialogClose();
    } catch (err) {
      setError("Failed to remove family member");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);

      if (!user || !memberToEdit) {
        throw new Error("Missing required information");
      }

      await updateFamilyMemberRole(
        user.familyId,
        memberToEdit.userId,
        selectedRole
      );

      // Update local state to reflect role change
      setFamilyMembers(
        familyMembers.map((member) =>
          member.userId === memberToEdit.userId
            ? { ...member, role: selectedRole }
            : member
        )
      );

      setSuccess(
        `${memberToEdit.firstName} ${memberToEdit.lastName}'s role updated to ${selectedRole}`
      );
      handleEditRoleDialogClose();
    } catch (err) {
      setError("Failed to update role");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFamilyName = async () => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);

      if (!user || !newFamilyName.trim()) {
        throw new Error("Family name is required");
      }

      await updateFamilyName(user.familyId, newFamilyName);
      setFamilyName(newFamilyName);
      setSuccess("Family name updated successfully");
      handleEditFamilyNameDialogClose();
    } catch (err) {
      setError("Failed to update family name");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWeekStartDay = async (newWeekStartDay: number) => {
    try {
      setError(null);
      setSuccess(null);
      setLoading(true);

      if (!user) {
        throw new Error("User not found");
      }

      await updateFamilySettings(user.familyId, {
        weekStartDay: newWeekStartDay,
      });
      setWeekStartDay(newWeekStartDay);
      setSuccess("Weekly start day updated successfully");
    } catch (err) {
      setError("Failed to update weekly start day");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const copyFamilyIdToClipboard = () => {
    navigator.clipboard.writeText(user?.familyId || "");
    setSuccess("Family ID copied to clipboard");

    // Auto-dismiss success message after 3 seconds
    setTimeout(() => {
      setSuccess(null);
    }, 3000);
  };

  const getRoleLabel = (role: string): string => {
    switch (role) {
      case "PARENT":
        return "Parent";
      case "CHILD":
        return "Child";
      case "TEACHER":
        return "Teacher";
      case "COACH":
        return "Coach";
      default:
        return role;
    }
  };

  const getRoleChipColor = (
    role: string
  ):
    | "primary"
    | "secondary"
    | "default"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    switch (role) {
      case "PARENT":
        return "primary";
      case "CHILD":
        return "info";
      case "TEACHER":
        return "success";
      case "COACH":
        return "warning";
      default:
        return "default";
    }
  };

  if (loading && familyMembers.length === 0) {
    return (
      <LoadingSpinner
        variant="simple"
        message="Loading Family Settings..."
        subMessage="Fetching family information and member data"
      />
    );
  }

  // Only parents can access family settings
  if (user?.role !== UserRole.PARENT) {
    return (
      <Box>
        <Alert severity="warning">
          Only parents can access family settings.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "none" }}>
      <Grid container spacing={3}>
        {/* Header Section */}
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            Family Settings
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
        </Grid>

        {/* Settings Cards */}
        {/* Family Information Card */}
        <Grid item xs={12} md={6} lg={8}>
          <Paper sx={{ p: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Family Information</Typography>
              <IconButton
                onClick={handleEditFamilyNameDialogOpen}
                color="primary"
                size="small"
              >
                <EditIcon />
              </IconButton>
            </Box>
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Family Name
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {familyName}
            </Typography>

            <Typography variant="subtitle1" gutterBottom>
              Family ID
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography variant="body1" sx={{ mr: 1 }}>
                {user?.familyId}
              </Typography>
              <IconButton
                onClick={copyFamilyIdToClipboard}
                size="small"
                color="primary"
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Share this ID with family members who want to join your family.
            </Typography>
          </Paper>
        </Grid>

        {/* Weekly Settings Card */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Weekly Settings
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Week Start Day
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                value={weekStartDay}
                onChange={(e) =>
                  handleUpdateWeekStartDay(Number(e.target.value))
                }
                disabled={loading}
                size="small"
              >
                <MenuItem value={0}>Sunday</MenuItem>
                <MenuItem value={1}>Monday</MenuItem>
                <MenuItem value={2}>Tuesday</MenuItem>
                <MenuItem value={3}>Wednesday</MenuItem>
                <MenuItem value={4}>Thursday</MenuItem>
                <MenuItem value={5}>Friday</MenuItem>
                <MenuItem value={6}>Saturday</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              This affects the weekly progress chart on the dashboard and weekly
              statistics calculations.
            </Typography>
          </Paper>
        </Grid>

        {/* Family Members List */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, minHeight: 250 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6">Family Members</Typography>
              <Button
                variant="contained"
                startIcon={<InviteIcon />}
                onClick={handleInviteDialogOpen}
              >
                Invite Member
              </Button>
            </Box>

            <List>
              {familyMembers.map((member) => (
                <React.Fragment
                  key={member.userId || (member as any).invitationId}
                >
                  <ListItem>
                    <ListItemAvatar>
                      {(member as any).status === "PENDING" ? (
                        <Avatar>
                          <PersonIcon />
                        </Avatar>
                      ) : (
                        <UserAvatar user={member} size={40} />
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1" sx={{ mr: 1 }}>
                            {/* Show name for active members or email for pending invitations */}
                            {(member as any).status === "PENDING"
                              ? member.email
                              : `${member.firstName} ${member.lastName}`}
                          </Typography>
                          <Chip
                            label={getRoleLabel(member.role)}
                            size="small"
                            color={getRoleChipColor(member.role)}
                          />
                          {/* Show status chip for pending invitations */}
                          {(member as any).status === "PENDING" && (
                            <Chip
                              label={
                                (member as any).isExpired
                                  ? "Expired"
                                  : "Pending"
                              }
                              size="small"
                              color={
                                (member as any).isExpired ? "error" : "warning"
                              }
                              sx={{ ml: 1 }}
                            />
                          )}
                          {/* Show "You" chip for current user */}
                          {member.userId === user?.userId && (
                            <Chip label="You" size="small" sx={{ ml: 1 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        (member as any).status === "PENDING"
                          ? `Invited ${new Date(
                              (member as any).createdAt
                            ).toLocaleDateString()}`
                          : member.email
                      }
                    />
                    {/* Show appropriate action buttons based on member status */}
                    {(member as any).status === "PENDING" ? (
                      // Resend button for pending invitations
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() =>
                            handleResendInvitation(
                              (member as any).invitationId,
                              member.email,
                              member.role
                            )
                          }
                          color="primary"
                          disabled={loading}
                          title="Resend invitation"
                        >
                          <ResendIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    ) : (
                      // Edit and remove buttons for active members (not current user)
                      member.userId &&
                      member.userId !== user?.userId && (
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => handleEditRoleDialogOpen(member)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleRemoveDialogOpen(member)}
                            color="error"
                            sx={{ ml: 1 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )
                    )}
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Invite Member Dialog */}
      <Dialog open={isInviteDialogOpen} onClose={handleInviteDialogClose}>
        <DialogTitle>Invite to Family</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="email"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            disabled={loading}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel id="invite-role-select-label">Role</InputLabel>
            <Select
              labelId="invite-role-select-label"
              id="invite-role-select"
              value={inviteRole}
              label="Role"
              onChange={(e) => setInviteRole(e.target.value as UserRole)}
              disabled={loading}
            >
              <MenuItem value={UserRole.PARENT}>Parent</MenuItem>
              <MenuItem value={UserRole.CHILD}>Child</MenuItem>
              <MenuItem value={UserRole.TEACHER}>Teacher</MenuItem>
              <MenuItem value={UserRole.COACH}>Coach</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInviteDialogClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleInvite} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Send Invitation"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog open={isRemoveDialogOpen} onClose={handleRemoveDialogClose}>
        <DialogTitle>Remove Family Member</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Are you sure you want to remove {memberToRemove?.firstName}{" "}
            {memberToRemove?.lastName} from your family?
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveDialogClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleRemoveMember}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={isEditRoleDialogOpen} onClose={handleEditRoleDialogClose}>
        <DialogTitle>Edit Member Role</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            {memberToEdit?.firstName} {memberToEdit?.lastName}
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              id="role-select"
              value={selectedRole}
              label="Role"
              onChange={(e) => setSelectedRole(e.target.value as UserRole)}
              disabled={loading}
            >
              <MenuItem value={UserRole.PARENT}>Parent</MenuItem>
              <MenuItem value={UserRole.CHILD}>Child</MenuItem>
              <MenuItem value={UserRole.TEACHER}>Teacher</MenuItem>
              <MenuItem value={UserRole.COACH}>Coach</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditRoleDialogClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRole}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Update Role"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Family Name Dialog */}
      <Dialog
        open={isEditFamilyNameDialogOpen}
        onClose={handleEditFamilyNameDialogClose}
      >
        <DialogTitle>Edit Family Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="familyName"
            label="Family Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newFamilyName}
            onChange={(e) => setNewFamilyName(e.target.value)}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditFamilyNameDialogClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateFamilyName}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Update Name"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FamilySettings;
